import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { billingAPI } from '../../services/api'
import { t } from '../../i18n'
import { PageHeader, Table, Loading, Empty, Chip, cents } from './ui'

const input = 'rounded-lg border-0 bg-white px-3 py-2 text-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-500'

export default function PlatformPlans() {
  const [plans, setPlans] = useState(null)
  const [form, setForm] = useState({ name: '', price: '', currency: 'usd' })
  const [saving, setSaving] = useState(false)

  const load = () => billingAPI.getPlans().then(r => setPlans(r.data.plans)).catch(() => setPlans([]))
  useEffect(() => { load() }, [])

  const create = async e => {
    e.preventDefault()
    const amount = Math.round(parseFloat(form.price) * 100)
    if (!form.name.trim() || !(amount > 0)) return toast.error(t('Enter a plan name and a price'))
    setSaving(true)
    try {
      await billingAPI.createPlan({ name: form.name.trim(), basePriceCents: amount, currency: form.currency, interval: 'month' })
      toast.success(t('Plan created'))
      setForm({ name: '', price: '', currency: form.currency })
      load()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to create the plan'))
    } finally { setSaving(false) }
  }

  const toggle = async plan => {
    try {
      await billingAPI.updatePlan(plan.id, { active: !plan.active })
      load()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to update the plan'))
    }
  }

  return (
    <div>
      <PageHeader title={t('Plans')} subtitle={t('Monthly prices businesses can subscribe to. You can still set a custom price for one business from its page.')} />

      <form onSubmit={create} className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-200">
        <label className="flex-1 min-w-[10rem]">
          <span className="mb-1 block text-xs font-medium text-slate-600">{t('Plan name')}</span>
          <input className={`${input} w-full`} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('e.g. Standard')} />
        </label>
        <label className="w-32">
          <span className="mb-1 block text-xs font-medium text-slate-600">{t('Price / month')}</span>
          <input className={`${input} w-full`} type="number" step="0.01" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="29.00" />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-slate-600">{t('Currency')}</span>
          <select className={input} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
            {['usd', 'eur', 'mad', 'gbp', 'sar', 'aed'].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </label>
        <button disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{t('Add plan')}</button>
      </form>

      {!plans ? <Loading /> : !plans.length ? <Empty>{t('No plans yet. Add your first one above.')}</Empty> : (
        <Table head={[t('Plan'), t('Price'), t('Status'), t('Stripe'), '']}>
          {plans.map(p => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
              <td className="px-4 py-3 tabular-nums">{cents(p.base_price_cents, (p.currency || 'usd').toUpperCase())}{t('/mo')}</td>
              <td className="px-4 py-3">{p.active ? <Chip tone="green">{t('Available')}</Chip> : <Chip>{t('Hidden')}</Chip>}</td>
              <td className="px-4 py-3">{p.stripe_price_id ? <Chip tone="blue">{t('Linked')}</Chip> : <span className="text-xs text-slate-500">{t('Linked on first checkout')}</span>}</td>
              <td className="px-4 py-3 text-end">
                <button onClick={() => toggle(p)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100">{p.active ? t('Hide') : t('Make available')}</button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}
